<script lang="ts" setup>
	import BaseModal from '@/components/modals/BaseModal.vue';
	import {useConsignmentStore} from '@/store/consignment.store';
	import {
		addOrderLineToShoppingCart,
		getRoundedValue,
		isSameConsignment,
		isSameVarietyAndWeek,
		removeOrderLineFromShoppingCart,
		updateOrderLineAmount,
		updateOrderLineConsignmentAndAmount
	} from 'dummen-shared';
	import {storeToRefs} from 'pinia';
	import {ref} from 'vue';
	import {useI18n} from 'vue-i18n';
	import Multiselect from 'vue-multiselect';
	import type {ShoppingCartOrderLine} from 'dummen-shared/src/interfaces/order.line.type.ts';
	import {useShoppingCartStore} from '@/store/shoppingcart.store.ts';

	const {t} = useI18n();

	const props = defineProps<{
		amount: number;
		orderLine: ShoppingCartOrderLine;
	}>();

	const shoppingCartStore = useShoppingCartStore();
	const consignmentStore = useConsignmentStore();

	const {consignments} = storeToRefs(consignmentStore);
	const splitAmount = ref(0);
	const consignmentLabel = ref('');

	const emits = defineEmits(['close']);

	const addTag = (newConsignment: string) => {
		consignments.value.push(newConsignment);
		consignmentLabel.value = newConsignment;
	};

	const setRoundedValue = (event: Event) => {
		const element = event.target as HTMLInputElement;
		splitAmount.value = getRoundedValue(
			Number.parseInt(element.value),
			props.amount,
			props.orderLine.packingUnit,
			props.orderLine.allowOverbooking
		);
	};

	const findOrderLineByConsignment = (consignment: string | null) => {
		return shoppingCartStore.getOrderLinesForWeek(props.orderLine.week, props.orderLine.year)
			.find((line) => isSameVarietyAndWeek(props.orderLine, line) && isSameConsignment(consignment, line.consignment));
	};

	const createOrderLineAfterSplit = (orderLine: ShoppingCartOrderLine, amount: number, consignment: string) => {
		if (!amount || amount < 1) {
			return;
		}

		orderLine.amount = amount;
		orderLine.consignment = consignment;

		addOrderLineToShoppingCart(orderLine);
	};

	const splitAmountToNewConsignment = () => {
		const newConsignment = consignmentLabel.value;
		const splitAmountValue = splitAmount.value;

		// Get the current order line where we are splitting from.
		const currentOrderLine = findOrderLineByConsignment(props.orderLine.consignment);

		if (!currentOrderLine) {
			return;
		}

		const currentOrderLineAmountAfterSplit = currentOrderLine.amount - splitAmountValue;

		// Update or remove the current order line after the split.
		if (currentOrderLineAmountAfterSplit <= 0) {
			removeOrderLineFromShoppingCart(currentOrderLine);
		} else {
			updateOrderLineAmount(currentOrderLine, currentOrderLineAmountAfterSplit);
		}

		// Check if an order line with the consignment already exists.
		const existingOrderLine = findOrderLineByConsignment(newConsignment);

		if (existingOrderLine) {
			updateOrderLineConsignmentAndAmount(existingOrderLine, newConsignment, splitAmountValue);
		} else {
			createOrderLineAfterSplit(currentOrderLine, splitAmountValue, newConsignment);
		}
	};

	const close = () => emits('close');

	const confirm = () => {
		splitAmountToNewConsignment();
		close();
	};
</script>

<template>
	<base-modal :cancel-button-text="t('shoppingcart.generic.cancel.text')"
	            :confirm-button-text="t('shoppingcart.modal.splitConsignment.split.title')"
	            :wide="false"
	            confirm-button-class="button--orange"
	            @cancel="close"
	            @confirm="confirm">

		<template v-slot:title>
			{{ t('shoppingcart.modal.splitConsignment.suffix.title') }}
		</template>

		<div class="shoppingcart-split-modal__content">
			<div class="shoppingcart-split-modal__order">
				{{ t('shoppingcart.modal.splitConsignment.split.text') }}
				<input v-model.number="splitAmount"
				       :max="amount"
				       :step="orderLine.packingUnit"
				       class="orderinput"
				       min="0"
				       type="number"
				       @blur="setRoundedValue" />
				{{ t('shoppingcart.modal.splitConsignment.of.text') }} {{ amount }}
			</div>

			<div class="shoppingcart-split-modal__to">to</div>

			<div class="shoppingcart-split-modal__consignment">
				<form novalidate onsubmit="return false;">
					<multiselect v-model="consignmentLabel"
					             :deselect-label="t('shoppingcart.generic.deselectConsignment.text')"
					             :options="consignments"
					             :placeholder="t('shoppingcart.generic.selectConsignment.text')"
					             :searchable="true"
					             :show-labels="true"
					             :taggable="true"
					             select-label=""
					             selected-label=""
					             @tag="addTag" />
				</form>
			</div>

		</div>
	</base-modal>
</template>
